// external libs
import React from 'react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Grid from '@material-ui/core/Grid'

// components
import MenuFolder from '../../../control/menuFolder/menuFolder'
import Dialog from '../../../control/dialog/dialog'
import ProjectInfomation from '../shareUI/editFormHeader/editFormHeader'
import DialogRequestConsiderDoc from './dialog/requestConsiderDoc/requestConsiderDoc'
import Messenger from './messenger/Messenger'
import ProjectHeaderActions from './projectHeaderActions/ProjectHeaderActions'

// hooks & stores
import useProjectManager from './useProjectManager'
import useAppFolderAndTabStore from '../../../stores/appFolderAndTab/appFolderAndTabStore'
import useCheckPermission from '../../../utilities/checkPermission'

// constants
import constSubmittedType from '../../../constants/submittedType'

// flows
import AdverseEvent from './flow/adverseEvents/adverseEvent'
import CloseProject from './flow/closeProject/closeProject'
import CloseProjectByAdmin from './flow/closeProjectByAdmin/closeProjectByAdmin'
import CloseProjectEditAfterReview from './flow/closeProjectEditAfterReview/closeProjectEditAfterReview'
import ProjectAmendment from './flow/projectAmendment/projectAmendment'
import ProjectProgressReport from './flow/projectProgressReport/projectProgressReport'
import ProjectProgressReportEditAfterReview from './flow/projectProgressReportEditAfterReview/projectProgressReportEditAfterReview'
import ProjectAmendmentEditAfterReview from './flow/projectAmendmentEditAfterReview/projectAmendmentEditAfterReview'
import ProjectFilesUpload from './flow/projectFilesUpload/projectFilesUpload'
import ProjectPhase from './flow/projectPhase/projectPhase'
import ProjectPhaseEditAfterReview from './flow/projectPhaseEditAfterReview/projectPhaseEditAfterReview'
import ProjectRenewalAndAmendment from './flow/projectRenewalAndAmendment/projectRenewalAndAmendment'
import ProjectRenewalAndAmendmentEditAfterReview from './flow/projectRenewalAndAmendmentEditAfterReview/projectRenewalAndAmendmentEditAfterReview'
import ProjectSiteVisitV2 from './flow/projectSiteVisitV2/projectSiteVisitV2'
import ProjectSiteVisitEditAfterReview from './flow/projectSiteVisitEditAfterReview/projectSiteVisitEditAfterReview'
import ProjectWithdrawal from './flow/projectWithdrawal/projectWithdrawal'
import ProjectTimeline from './projectTimeline/ProjectTimeline'
import ReportedNonconformance from './flow/reportedNonconformance/reportedNonconformance'
import RequestNewProject from './flow/requestNewProject/requestNewProject'
import RequestNewProjectEditAfterReview from './flow/requestNewProjectEditAfterReview/requestNewProjectEditAfterReview'
import RequestProjectRenewal from './flow/requestProjectRenewal/requestProjectRenewal'
import RequestProjectRenewalEditAfterReview from './flow/requestProjectRenewalEditAfterReview/requestProjectRenewalEditAfterReview'
import SubmittedWithdrawal from './flow/submittedWithdrawal/submittedWithdrawal'
import TerminateProject from './flow/terminateProject/terminateProject'
import TerminateProjectByAdmin from './flow/terminateProjectByAdmin/terminateProjectByAdmin'
import TerminateProjectEditAfterReview from './flow/terminateProjectEditAfterReview/terminateProjectEditAfterReview'
import useFolderAndTab from './useFolderAndTab'

type ProjectManagerViewType = ReturnType<typeof useProjectManager> & ProjectManagerType
const ProjectManagerView: React.FC<ProjectManagerViewType> = (props) => {
    const { dialogRequestConsiderDocRef, projectMode, projectData, onClickFolder, onRefDialog, configDialog } = props
    const classes = useStyles()
    const appFolderAndTabStore = useAppFolderAndTabStore()
    const checkPermission = useCheckPermission()
    const tabAndFolderHook = useFolderAndTab()

    const isUserResearcher = checkPermission.isUserResearcher()
    const isUserAdmin = checkPermission.isUserAdmin()
    const isUserCommittee = checkPermission.isUserCommittee()
    const isCustomFolder = tabAndFolderHook.isCustomFolder(appFolderAndTabStore.folderSelected.id ?? 0)

    const isProjectTimelineVisible = !isUserResearcher && !isUserCommittee
    const isMessengerVisible = (isUserResearcher || isUserAdmin) && projectData?.isShowMessenger

    const renderFlow = () => {
        const submittedTypeID = projectData?.submitTypeInfo?.value
        switch (submittedTypeID) {
            // *ส่งพิจารณาครั้งแรก
            case constSubmittedType.firstSubmitConsideration:
            case null:
            case undefined:
                return <RequestNewProject projectMode={projectMode} />
            // *แก้ไขหลังพิจารณา
            case constSubmittedType.editAfterReview:
                return <RequestNewProjectEditAfterReview projectMode={projectMode} />
            // *โครงการระยะถัดไป
            case constSubmittedType.nextPhaseProject:
                return <ProjectPhase projectMode={projectMode} />
            // *ขอต่ออายุโครงการ
            case constSubmittedType.renewalProject:
                return <RequestProjectRenewal projectMode={projectMode} />
            // *ขอแก้ไขโครงการ
            case constSubmittedType.amendmentProject:
                return <ProjectAmendment projectMode={projectMode} />
            // *รายงานเหตุการณ์ที่ไม่พึงประสงค์
            case constSubmittedType.adverseEvent:
                return <AdverseEvent projectMode={projectMode} />
            // *รายงานที่ไม่เป็นไปตามข้อกำหนด
            case constSubmittedType.nonconformance:
                return <ReportedNonconformance projectMode={projectMode} />
            // *แก้ไขหลังพิจารณา (ขอแก้ไขโครงการ)
            case constSubmittedType.exitAfterReview_AmendmentProject:
                return <ProjectAmendmentEditAfterReview projectMode={projectMode} />
            // *ขอปิดโครงการ
            case constSubmittedType.closeProject:
                return <CloseProject projectMode={projectMode} />
            // *ขอยุติโครงการ
            case constSubmittedType.terminateProject:
                return <TerminateProject projectMode={projectMode} />
            // *แก้ไขหลังพิจารณา (โครงการระยะถัดไป)
            case constSubmittedType.editAfterReview_nextPhaseProject:
                return <ProjectPhaseEditAfterReview projectMode={projectMode} />
            // *แก้ไขหลังพิจารณา (ขอปิดโครงการ)
            case constSubmittedType.editAfterReview_closeProject:
                return <CloseProjectEditAfterReview projectMode={projectMode} />
            // *ขอปิดโครงการ (โดยเจ้าหน้าที่)
            case constSubmittedType.closeProjectByAdmin:
                return <CloseProjectByAdmin projectMode={projectMode} />
            // *แก้ไขหลังพิจารณา (ขอยุติโครงการ)
            case constSubmittedType.editAfterReview_terminateProject:
                return <TerminateProjectEditAfterReview projectMode={projectMode} />
            // *ขอยุติโครงการ (โดยเจ้าหน้าที่)
            case constSubmittedType.terminateProjectByAdmin:
                return <TerminateProjectByAdmin projectMode={projectMode} />
            // *ถอนโครงการ (การถอนทั้งโครงการ)
            case constSubmittedType.withdrawalProject:
                return <ProjectWithdrawal projectMode={projectMode} />
            // *ถอนโครงการ   (การถอนเฉพาะการยื่นครั้งนั้น)
            case constSubmittedType.withdrawalSubmitted:
                return <SubmittedWithdrawal projectMode={projectMode} />
            // *แก้ไขหลังพิจารณา (ต่ออายุ)
            case constSubmittedType.editAfterReview_renewalProject:
                return <RequestProjectRenewalEditAfterReview projectMode={projectMode} />
            // *ขอแก้ไขพร้อมต่ออายุโครงการ
            case constSubmittedType.renewalAndAmendmentProject:
                return <ProjectRenewalAndAmendment projectMode={projectMode} />
            // *แก้ไขหลังพิจารณา (แก้ไขพร้อมต่ออายุโครงการ )
            case constSubmittedType.editAfterReviewRenewalAndAmendmentProject:
                return <ProjectRenewalAndAmendmentEditAfterReview projectMode={projectMode} />
            // *รายงานความก้าวหน้าโครงการที่มีความเสี่ยงสูง (ใหม่)
            case constSubmittedType.highRiskProjectProgressReport:
                return <ProjectProgressReport projectMode={projectMode} />
            // *แก้ไขหลังพิจารณา (รายงานความก้าวหน้าโครงการที่มีความเสี่ยงสูง)
            case constSubmittedType.editAfterReviewHighRiskProjectProgressReport:
                return <ProjectProgressReportEditAfterReview projectMode={projectMode} />
            // *เอกสารสำหรับตรวจเยี่ยม
            case constSubmittedType.projectSiteVisit:
                return <ProjectSiteVisitV2 projectMode={projectMode} />
            // *แก้ไขหลังพิจารณา (เอกสารสำหรับตรวจเยี่ยม)
            case constSubmittedType.editAfterReviewProjectSiteVisit:
                return <ProjectSiteVisitEditAfterReview projectMode={projectMode} />
            // *อัปโหลดเอกสาร
            case constSubmittedType.projectFilesUpload:
                return projectData ? <ProjectFilesUpload projectID={projectData.projectID} /> : null
            default:
                return (
                    <>
                        No avilable flow for submit type id: {projectData?.submitTypeInfo.value}, Please contact
                        administrator.
                    </>
                )
        }
    }
    return (
        <Grid container className={classes.root} alignContent="flex-start">
            {projectData ? (
                <>
                    <ProjectHeaderActions project={projectData} />
                    {isProjectTimelineVisible && !isCustomFolder ? <ProjectTimeline project={projectData} /> : null}
                    <ProjectInfomation projectData={projectData} />
                </>
            ) : null}

            <Grid item xs={12}>
                <MenuFolder
                    menuFolderList={appFolderAndTabStore.folderList}
                    folderSelected={appFolderAndTabStore.folderSelected.id}
                    onClickMenu={onClickFolder}
                >
                    {renderFlow()}
                </MenuFolder>
            </Grid>

            <Dialog onRef={onRefDialog} {...configDialog} />
            <DialogRequestConsiderDoc bindRef={dialogRequestConsiderDocRef} />

            {isMessengerVisible && projectData ? <Messenger project={projectData} /> : null}
        </Grid>
    )
}

export type ProjectManagerType = {}
const ProjectManager: React.FC<ProjectManagerType> = (props) => {
    const projectManager = useProjectManager(props)
    return <ProjectManagerView {...projectManager} {...props} />
}

export default ProjectManager

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
        height: '100%',
    },
}))
